import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Tooltip, TableSortLabel, Collapse
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { F } from '../../../utils/qualFmt';


function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function formatSegmentation(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const parts = [];
  Object.entries(seg).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) {
      const list = v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
      parts.push(`${k}: ${list}`);
    } else if (typeof v === 'object') {
      const inner = Object.entries(v)
        .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
        .join('; ');
      parts.push(`${k}: { ${inner} }`);
    } else {
      parts.push(`${k}: ${String(v)}`);
    }
  });
  return parts.length ? parts.join(' • ') : '—';
}

export default function SiblingProductsTable({ rows = [] }) {
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('count');
  const [open, setOpen] = React.useState({});

  const handleSort = (prop) => () => {
    const isAsc = orderBy === prop && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(prop);
  };

  const sorted = React.useMemo(
    () => rows.slice().sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  const toggle = (key) => setOpen((s) => ({ ...s, [key]: !s[key] }));

  if (!rows.length) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No sibling solutions found for these jobs.
      </Typography>
    );
  }

  return (
    <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell sortDirection={orderBy === 'name' ? order : false}>
              <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={handleSort('name')}>
                Solution
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sortDirection={orderBy === 'count' ? order : false}>
              <TableSortLabel active={orderBy === 'count'} direction={orderBy === 'count' ? order : 'desc'} onClick={handleSort('count')}>
                Occurrences
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sortDirection={orderBy === 'pct' ? order : false}>
              <TableSortLabel active={orderBy === 'pct'} direction={orderBy === 'pct' ? order : 'desc'} onClick={handleSort('pct')}>
                Share
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sortDirection={orderBy === 'avgSatisfaction' ? order : false}>
              <TableSortLabel active={orderBy === 'avgSatisfaction'} direction={orderBy === 'avgSatisfaction' ? order : 'desc'} onClick={handleSort('avgSatisfaction')}>
                Avg satisfaction
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sorted.map((r, idx) => {
            const key = r.name;
            const avgNum = Number.isFinite(r.avgSatisfaction) ? r.avgSatisfaction : null;
            const qual = avgNum != null ? F.productSatisfaction(avgNum).text : '';
            return (
              <React.Fragment key={`sib-${idx}`}>
                <TableRow hover>
                  <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => toggle(key)} aria-label="Expand row">
                      {open[key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                  </TableCell>
                  <TableCell align="right">{r.count}</TableCell>
                  <TableCell align="right">{r.pct}%</TableCell>
                  <TableCell align="right">
                    {avgNum != null ? qual : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Open Product">
                      <span>
                        <IconButton size="small" component="a" href={r.href} aria-label="Open product">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Expanded sub-rows: interviews that featured this sibling product in those jobs */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={!!open[key]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, pl: 6 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Interviews
                        </Typography>

                        {Array.isArray(r.instances) && r.instances.length > 0 ? (
                          <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Segmentation</TableCell>
                                <TableCell>Job</TableCell>
                                <TableCell align="right">Satisfaction</TableCell>
                                <TableCell>Summary</TableCell>
                                <TableCell align="center" width={56}>Open</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {r.instances.map((inst, i) => {
                                const sat = Number.isFinite(inst.satisfaction) ? inst.satisfaction : null;
                                const satQual = sat != null ? F.productSatisfaction(sat).text : '';
                                return (
                                  <TableRow key={`${key}-inst-${i}`} hover>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {inst.name || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {inst.date || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {formatSegmentation(inst.segmentation)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {inst.job || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {sat != null ? satQual : '—'}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {inst.summary || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Open interview">
                                        <span>
                                          <IconButton
                                            size="small"
                                            component="a"
                                            href={inst.href}
                                            target="_blank"
                                            rel="noopener"
                                            aria-label="Open interview"
                                          >
                                            <OpenInNewIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No interviews found for this solution.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
